const DESCRIPTION_KEYS = [
  { label: "Definition", value: "definition" },
  { label: "Control", value: "control" },
  { label: "Alias", value: "alias" },
  { label: "Type", value: "type" },
  { label: "Comments", value: "comment" },
  { label: "Invariants", value: "constraint" },
];

const PROFILE_KEYS = [
  { key: "baseDefinition", label: "Official URL", isURL: true },
  { key: "copyright", label: "Copyright" },
  { key: "fhirVersion", label: "Version" },
  { key: "name", label: "Computable Name" },
  { key: "date", label: "Active as of" },
];

const INFORMATION_GUIDE_TABS = [
  { tabName: "content", label: "Content" },
  { tabName: "description", label: "Detailed Description" },
  { tabName: "json", label: "JSON" },
  { tabName: "examples", label: "Examples" },
];

const CONTENT_TABS = [
  { tabName: "differentialTable", label: "Differential Table" },
  { tabName: "snapshot", label: "Snapshot" },
];

const FHIR_RESOURCE_TYPES = [
  {
    title: "AllergyIntolerance",
    resources: [{ label: "Allergy Intolerance", value: "allergyintolerance" }],
  },
  {
    title: "CarePlan",
    resources: [{ label: "Care Plan", value: "careplan" }],
  },
  {
    title: "CareTeam",
    resources: [{ label: "Care Team", value: "careteam" }],
  },
  {
    title: "Condition",
    resources: [
      {
        label: "Condition Encounter Diagnosis",
        value: "condition-encounter-diagnosis",
      },
      {
        label: "Condition Problems and Health Concerns",
        value: "condition-problems-health-concerns",
      },
    ],
  },
  {
    title: "Device",
    resources: [{ label: "Device", value: "implantable-device" }],
  },
  {
    title: "DiagnosticReport",
    resources: [
      {
        label: "Diagnostic Report - Laboratory Results Reporting",
        value: "diagnosticreport-lab",
      },
      {
        label: "DiagnosticReport - Report and Note Exchange",
        value: "diagnosticreport-note",
      },
    ],
  },
  {
    title: "DocumentReference",
    resources: [{ label: "Document Reference", value: "documentreference" }],
  },
  {
    title: "Encounter",
    resources: [{ label: "Encounter", value: "encounter" }],
  },
  {
    title: "Goal",
    resources: [{ label: "Goal", value: "goal" }],
  },
  {
    title: "Immunization",
    resources: [{ label: "Immunization", value: "immunization" }],
  },
  {
    title: "Location",
    resources: [{ label: "Location", value: "location" }],
  },
  {
    title: "Medication",
    resources: [{ label: "Medication", value: "medication" }],
  },
  {
    title: "MedicationRequest",
    resources: [{ label: "Medication Request", value: "medicationrequest" }],
  },
  {
    title: "Observation",
    resources: [
      {
        label: "Observation Clinical Test Result",
        value: "observation-clinical-test",
      },
      { label: "Observation Imaging Result", value: "observation-imaging" },
      { label: "Laboratory Result Observation", value: "observation-lab" },
      {
        label: "Observation Sexual Orientation",
        value: "observation-sexual-orientation",
      },
      {
        label: "Observation Social History",
        value: "observation-social-history",
      },
      { label: "Observation Survey", value: "observation-survey" },
      {
        label: "Observation SDOH Assessment",
        value: "observation-sdoh-assessment",
      },
      { label: "Core Smoking Status Observation", value: "smokingstatus" },
      { label: "Pediatric BMI for Age", value: "pediatric-bmi-for-age" },
      {
        label: "Pediatric Weight for Height",
        value: "pediatric-weight-for-height",
      },
      { label: "Heart Rate", value: "heart-rate" },
      { label: "Respiratory Rate", value: "respiratory-rate" },
      { label: "Blood Pressure", value: "blood-pressure" },
      {
        label: "Pediatric Head Occipital-frontal Circumference Percentile",
        value: "head-occipital-frontal-circumference-percentile",
      },
      { label: "Body Height", value: "body-height" },
      { label: "Head Circumference", value: "head-circumference" },
      { label: "Body Temperature", value: "body-temperature" },
      { label: "BMI", value: "bmi" },
      { label: "Pulse Oximetry", value: "pulse-oximetry" },
      { label: "Body Weight", value: "body-weight" },
      { label: "Vital Signs", value: "vital-signs" },
    ],
  },

  {
    title: "Organization",
    resources: [{ label: "Organization", value: "organization" }],
  },
  {
    title: "Patient",
    resources: [{ label: "Patient", value: "patient" }],
  },
  {
    title: "Practitioner",
    resources: [{ label: "Practitioner", value: "practitioner" }],
  },
  {
    title: "PractitionerRole",
    resources: [{ label: "Practitioner Role", value: "practitionerrole" }],
  },
  {
    title: "Procedure",
    resources: [{ label: "Procedure", value: "procedure" }],
  },
  {
    title: "Provenance",
    resources: [{ label: "Provenance", value: "provenance" }],
  },
  {
    title: "QuestionnaireResponse",
    resources: [
      { label: "Questionnaire Response", value: "questionnaireresponse" },
    ],
  },
  {
    title: "RelatedPerson",
    resources: [{ label: "Related Person", value: "relatedperson" }],
  },
  {
    title: "ServiceRequest",
    resources: [{ label: "Service Request", value: "servicerequest" }],
  },
];

const RESOURCE_PARAMETERS = [
  {
    label: "Category",
    value: "category",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Criticality",
    value: "criticality",
  },
  {
    label: "Status",
    value: "status",
  },
  {
    label: "Type",
    value: "type",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Subject",
    value: "subject",
  },
  {
    label: "Patient",
    value: "patient",
  },
  {
    label: "Resource Type",
    value: "resourceType",
  },
  {
    label: "Clinical Status",
    value: "clinicalStatus",
    keysToAvoid: ["system", "display", "text"],
  },
  {
    label: "Verification Status",
    value: "verificationStatus",
    keysToAvoid: ["system", "display", "text"],
  },
  {
    label: "Code",
    value: "code",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Name",
    value: "name",
    keysToAvoid: ["use", "period"],
  },
  {
    label: "Telecom",
    value: "telecom",
    keysToAvoid: ["system", "use"],
  },
  {
    label: "Address",
    value: "address",
    keysToAvoid: ["use"],
  },
  {
    label: "Manufacturer",
    value: "manufacturer",
  },
  {
    label: "Recorded Date",
    value: "recordedDate",
  },
  {
    label: "Description",
    value: "description",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Author",
    value: "author",
    keysToAvoid: ["reference"],
  },
  {
    label: "Identifier",
    value: "identifier",
    keysToAvoid: ["system", "code", "use", "value"],
  },
  {
    label: "Encounter",
    value: "encounter",
  },
  {
    label: "Effective",
    value: "effectiveDateTime",
  },
  {
    label: "Value",
    value: "valueString",
  },
  {
    label: "Birth Date",
    value: "birthDate",
  },
  {
    label: "Relationship",
    value: "relationship",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Abatement",
    value: "abatementDateTime",
  },
  {
    label: "onset",
    value: "onsetDateTime",
  },
  {
    label: "Expiration Date",
    value: "expirationDate",
  },
  {
    label: "Serial Number",
    value: "serialNumber",
  },
  {
    label: "Lot Number",
    value: "lotNumber",
  },
  {
    label: "Class",
    value: "class",
    keysToAvoid: ["system", "code"],
  },
  {
    label: "Life Cycle Status",
    value: "lifecycleStatus",
  },
  {
    label: "occurrenceDateTime",
    value: "Occurrence",
  },
  {
    label: "Vaccine Code",
    value: "vaccineCode",
    keysToAvoid: ["system", "code", "display"],
  },
  {
    label: "Primary Source",
    value: "primarySource",
  },
  {
    label: "Medication",
    value: "medicationCodeableConcept",
    keysToAvoid: ["coding"],
  },
  {
    label: "authoredOn",
    value: "authoredOn",
  },
];

export {
  PROFILE_KEYS,
  INFORMATION_GUIDE_TABS,
  CONTENT_TABS,
  RESOURCE_PARAMETERS,
  DESCRIPTION_KEYS,
  FHIR_RESOURCE_TYPES,
};
